import { motion } from 'framer-motion';
import Image from 'next/image';
import { cn } from "@/lib/utils";

const testimonials = [
    {
        quote: "Wow, this is absolutely incredible – huge props to the Deckwale team for bringing these amazing AI superpowers to more founders!",
        name: "Alex Rivera",
        role: "@arivera_tech",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
        quote: "Finally an AI tool that I will ACTUALLY include as part of my workflow. I love how seamless it is from start to finish.",
        name: "Sarah Chen",
        role: "Product Designer",
        avatar: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
        quote: "This is pure magic. ✨ Sitemaps in seconds → Wireframes in minutes → Full pitch deck written with AI. Saving the day!",
        name: "Marcus Johnson",
        role: "@mjohnson_design",
        avatar: "https://randomuser.me/api/portraits/men/22.jpg"
    },
    {
        quote: "Bro what?! Are you telling I was able to produce all of this with just one prompt? As soon as that happened, I signed up for the Pro plan RIGHT AWAY.",
        name: "David Smith",
        role: "Founder & CEO",
        avatar: "https://randomuser.me/api/portraits/men/86.jpg"
    },
    {
        quote: "Very excited for Deckwale's new AI-powered generative builder. I love the messaging and positioning: 'to empower you, not replace you'.",
        name: "Emily Davis",
        role: "Marketing Director",
        avatar: "https://randomuser.me/api/portraits/women/68.jpg"
    },
    {
        quote: "This is the most exciting AI product I've seen — great work Deckwale team!!! Looks especially powerful for serious founders looking to build for scale.",
        name: "Michael Brown",
        role: "@mbrown_vc",
        avatar: "https://randomuser.me/api/portraits/men/54.jpg"
    },
    {
        quote: "The new AI Sitemap and Wireframe tools are game changers! This can absolutely speed up our development.",
        name: "Jessica Wilson",
        role: "Product Manager",
        avatar: "https://randomuser.me/api/portraits/women/29.jpg"
    },
    {
        quote: "Been sitting here with my jaw on the floor - periodically saying 'oh my god, no way' after checking out Deckwale.",
        name: "Chris Taylor",
        role: "Startup Mentor",
        avatar: "https://randomuser.me/api/portraits/men/46.jpg"
    },
    {
        quote: "Deckwale bringing the heat 🔥 Big big fan. Been using it on client projects to build sitemaps.",
        name: "Ashley White",
        role: "@awhite_creative",
        avatar: "https://randomuser.me/api/portraits/women/12.jpg"
    },
    {
        quote: "Yoooooooo, this is 🔥 Deckwale has been one of my favorite teams I've seen evolve in the space.",
        name: "James Martin",
        role: "Tech Blogger",
        avatar: "https://randomuser.me/api/portraits/men/18.jpg"
    },
    {
        quote: "Just tried the new features and I'm blown away. The accuracy of the AI is unmatched.",
        name: "Sophia Lee",
        role: "UX Researcher",
        avatar: "https://randomuser.me/api/portraits/women/90.jpg"
    },
    {
        quote: "Game changer for my agency. We can now deliver prototypes in half the time.",
        name: "Daniel Kim",
        role: "Agency Owner",
        avatar: "https://randomuser.me/api/portraits/men/76.jpg"
    }
];

const Testimonials = () => {
    // Split testimonials into 3 columns for desktop, 2 for tablet, 1 for mobile
    const column1 = testimonials.slice(0, 4);
    const column2 = testimonials.slice(4, 8);
    const column3 = testimonials.slice(8, 12);

    return (
        <section className="py-24 bg-[#ffffff] relative overflow-hidden">
            <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center mb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="max-w-3xl mx-auto"
                >
                    <h2 className="text-[48px] md:text-[56px] font-bold text-gray-900 font-syne leading-tight tracking-tight mb-6">
                        Loved by <span className="text-[#2563EB]">creative people</span>
                    </h2>
                    <p className="text-gray-500 font-jakarta text-lg max-w-2xl mx-auto">
                        Join thousands of founders and creators who are building faster with Deckwale.
                    </p>
                </motion.div>
            </div>

            {/* Testimonials Grid with Marquee Effect */}
            <div className="relative h-[800px] overflow-hidden">
                {/* Gradient Masks */}
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#ffffff] to-transparent z-20 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#ffffff] to-transparent z-20 pointer-events-none" />

                <div className="max-w-[1100px] mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 h-full">
                    <MarqueeColumn items={column1} duration={45} />
                    <MarqueeColumn items={column2} duration={55} className="hidden md:block" />
                    <MarqueeColumn items={column3} duration={50} className="hidden lg:block" />
                </div>
            </div>
        </section>
    );
};

const MarqueeColumn = ({ items, duration, className }: { items: typeof testimonials, duration: number, className?: string }) => {
    return (
        <div className={cn("relative h-full overflow-hidden", className)}>
            <motion.div
                animate={{ y: ["0%", "-50%"] }}
                transition={{
                    repeat: Infinity,
                    ease: "linear",
                    duration: duration,
                }}
                className="flex flex-col gap-6 pb-6"
            >
                {/* Double the items to create seamless loop */}
                {[...items, ...items, ...items].map((testimonial, idx) => (
                    <TestimonialCard key={idx} testimonial={testimonial} />
                ))}
            </motion.div>
        </div>
    );
};

const TestimonialCard = ({ testimonial }: { testimonial: typeof testimonials[0] }) => {
    return (
        <div className="bg-[#ffffff] p-6 rounded-2xl border border-gray-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-lg hover:border-blue-100 transition-all duration-300">
            <p className="text-gray-800 font-jakarta text-[15px] leading-relaxed mb-6 font-medium">
                "{testimonial.quote}"
            </p>
            <div className="flex items-center gap-3">
                <Image
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-full object-cover border border-gray-100"
                />
                <div>
                    <p className="font-syne font-bold text-gray-900 text-sm">
                        {testimonial.name}
                    </p>
                    <p className="text-xs font-jakarta text-gray-500 font-medium">
                        {testimonial.role}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Testimonials;
